// Theme and density of this browser (design-system.md §2.5): stored locally, applied to <html>. The density an app
// starts from is the `data-density` of its index.html: compact in Backstage, comfortable in Atelier (ADR-070).

export type Theme = "light" | "dark" | "system";
export type Density = "compact" | "comfortable";

const THEME_KEY = "nexxt.theme";
const DENSITY_KEY = "nexxt.density";

let initialDensity: Density | undefined;
/** The density of index.html, read on the first apply, before the stored choice replaces it. */
const appDensity = (): Density =>
  (initialDensity ??= (document.documentElement.dataset.density as Density | undefined) ?? "comfortable");

export const preferences = {
  theme: (): Theme => (localStorage.getItem(THEME_KEY) as Theme | null) ?? "system",
  density: (): Density => (localStorage.getItem(DENSITY_KEY) as Density | null) ?? appDensity(),
  setTheme(theme: Theme) {
    localStorage.setItem(THEME_KEY, theme);
    applyPreferences();
  },
  setDensity(density: Density) {
    localStorage.setItem(DENSITY_KEY, density);
    applyPreferences();
  },
};

/**
 * Whether a theme shows dark: the stored one by default, or the one a page holds in its state, since the React
 * Compiler keeps the result of a call without inputs.
 */
export function isDark(theme: Theme = preferences.theme()): boolean {
  return theme === "dark" || (theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
}

export function applyPreferences(): void {
  const root = document.documentElement;
  appDensity();
  root.classList.toggle("dark", isDark());
  root.dataset.density = preferences.density();
}
